import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DataGrid } from "@mui/x-data-grid";
import { format } from "date-fns";
import api from "../utils/api";
import { buildAuditCsvRows, toCsvContent } from "../utils/auditExport";

const Audit = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    user: "",
    action: "",
    table: "",
    startDate: null,
    endDate: null,
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [detailLog, setDetailLog] = useState(null);

  const fetchAuditLogs = useCallback(
    async (searchParam = null, exportMode = false) => {
      try {
        if (!exportMode) {
          setLoading(true);
        }
        const params = {
          page: exportMode ? 1 : pagination.page + 1,
          limit: exportMode ? 10000 : pagination.pageSize, // Large limit for export
          ...filters,
        };

        if (filters.startDate) {
          params.startDate = filters.startDate.toISOString().split("T")[0];
        }
        if (filters.endDate) {
          params.endDate = filters.endDate.toISOString().split("T")[0];
        }

        // Add search parameter if provided
        if (searchParam !== null) {
          params.search = searchParam;
        } else if (searchText) {
          params.search = searchText;
        }

        const response = await api.get("/api/audit", { params });
        return response.data;
      } catch (error) {
        console.error("Failed to fetch audit logs:", error);
        const errorMessage =
          error.response?.data?.details ||
          error.response?.data?.error ||
          error.message ||
          "Failed to load audit logs";
        if (!exportMode) {
          setError(errorMessage);
        }
        throw error;
      } finally {
        if (!exportMode) {
          setLoading(false);
        }
      }
    },
    [filters, pagination.page, pagination.pageSize, searchText],
  );

  // Fetch logs for display
  useEffect(() => {
    fetchAuditLogs(null, false)
      .then((data) => {
        if (data) {
          setLogs(data.logs);
          setPagination((prev) => ({
            ...prev,
            total: data.pagination.total,
          }));
        }
      })
      .catch(() => {
        // Error already handled in fetchAuditLogs
      });
  }, [fetchAuditLogs]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  // Handle search text change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 0 }));
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const getActionColor = (action) => {
    switch (action) {
      case "CREATE":
        return "success";
      case "UPDATE":
        return "warning";
      case "DELETE":
        return "error";
      case "READ":
        return "info";
      case "LOGIN":
        return "primary";
      case "LOGOUT":
        return "default";
      default:
        return "default";
    }
  };

  // Export to CSV - fetches all matching records from server
  const exportToCSV = async () => {
    try {
      setLoading(true);

      // Build search parameter - combine searchText with existing filters
      const searchParam = searchText || null;

      // Fetch all matching records (with large limit)
      const data = await fetchAuditLogs(searchParam, true);

      if (!data || !data.logs || data.logs.length === 0) {
        alert("No data to export");
        setLoading(false);
        return;
      }

      const { headers, rows } = buildAuditCsvRows(data.logs, (createdAt) =>
        format(new Date(createdAt), "dd MMM yyyy HH:mm:ss"),
      );
      const csvContent = toCsvContent(headers, rows);

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const filename = `audit_logs_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}${searchText ? `_search_${searchText}` : ""}.csv`;
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      const detail =
        error.response?.data?.details ||
        error.response?.data?.error ||
        error.message;
      alert(
        detail
          ? `Failed to export audit logs: ${detail}`
          : "Failed to export audit logs. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fallbackSummary = (row) => {
    const a = row.action || "Action";
    const p = row.page_label || row.table_name || "—";
    const r = row.record_id != null ? ` #${row.record_id}` : "";
    return `${a} ${p}${r}`;
  };

  const columns = [
    {
      field: "created_at",
      headerName: "Timestamp",
      width: 170,
      minWidth: 150,
      renderCell: (params) =>
        format(new Date(params.value), "dd MMM yyyy HH:mm:ss"),
    },
    {
      field: "summary",
      headerName: "What changed",
      flex: 2,
      minWidth: 260,
      renderCell: (params) => (
        <Typography
          variant="body2"
          component="span"
          title={params.value || fallbackSummary(params.row)}
        >
          {params.value || fallbackSummary(params.row)}
        </Typography>
      ),
    },
    {
      field: "page_label",
      headerName: "Page / Area",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => params.value || params.row.table_name || "—",
    },
    {
      field: "user_name",
      headerName: "User",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => params.value || "System",
    },
    {
      field: "action",
      headerName: "Action",
      width: 110,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getActionColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "record_id",
      headerName: "Record ID",
      width: 100,
      minWidth: 90,
      renderCell: (params) => (params.value != null ? params.value : "—"),
    },
    {
      field: "ip_address",
      headerName: "IP Address",
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "_details",
      headerName: " ",
      width: 110,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setDetailLog(params.row);
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
      <Box width="100%">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3, width: "100%" }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={filters.action}
                    label="Action"
                    onChange={(e) =>
                      handleFilterChange("action", e.target.value)
                    }
                  >
                    <MenuItem value="">All Actions</MenuItem>
                    <MenuItem value="CREATE">Create</MenuItem>
                    <MenuItem value="UPDATE">Update</MenuItem>
                    <MenuItem value="DELETE">Delete</MenuItem>
                    <MenuItem value="READ">Read</MenuItem>
                    <MenuItem value="LOGIN">Login</MenuItem>
                    <MenuItem value="LOGOUT">Logout</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Page / Area</InputLabel>
                  <Select
                    value={filters.table}
                    label="Page / Area"
                    onChange={(e) =>
                      handleFilterChange("table", e.target.value)
                    }
                  >
                    <MenuItem value="">All areas</MenuItem>
                    <MenuItem value="licenses">Licenses</MenuItem>
                    <MenuItem value="license_categories">Categories</MenuItem>
                    <MenuItem value="license_reminders">Reminders</MenuItem>
                    <MenuItem value="users">Users</MenuItem>
                    <MenuItem value="suppliers">Supplier Lists</MenuItem>
                    <MenuItem value="clients">Clients</MenuItem>
                    <MenuItem value="ai_systems">AI Systems Register</MenuItem>
                    <MenuItem value="asset_register">Asset Register</MenuItem>
                    <MenuItem value="attestations">Attestations</MenuItem>
                    <MenuItem value="attestation_schedule_config">
                      Attestation schedule
                    </MenuItem>
                    <MenuItem value="certs_and_secrets">
                      Certs &amp; Secrets
                    </MenuItem>
                    <MenuItem value="software_records">
                      Software &amp; Permissions
                    </MenuItem>
                    <MenuItem value="cron_schedule_config">
                      Cron Schedules
                    </MenuItem>
                    <MenuItem value="azure_tenant_configs">
                      Azure tenant
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange("startDate", date)}
                  textField={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange("endDate", date)}
                  textField={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setFilters({
                      user: "",
                      action: "",
                      table: "",
                      startDate: null,
                      endDate: null,
                    });
                  }}
                  fullWidth
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Search and Export */}
        <Card sx={{ mb: 3, width: "100%" }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8} md={10}>
                <TextField
                  fullWidth
                  label="Search (case-sensitive)"
                  placeholder="Search across all columns..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={exportToCSV}
                  disabled={loading}
                >
                  Export CSV
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Data Grid */}
        <Card sx={{ width: "100%" }}>
          <CardContent sx={{ width: "100%", "&:last-child": { pb: 2 } }}>
            <Box height="calc(100vh - 340px)" minHeight={420} width="100%">
              <DataGrid
                rows={logs}
                columns={columns}
                loading={loading}
                pagination
                paginationMode="server"
                rowCount={pagination.total}
                paginationModel={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                onPaginationModelChange={(newModel) => {
                  setPagination((prev) => ({
                    ...prev,
                    page: newModel.page,
                    pageSize: newModel.pageSize,
                  }));
                }}
                disableSelectionOnClick
                disableColumnMenu
                getRowId={(row) => row.id}
                sx={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                  "& .MuiDataGrid-main": { width: "100%" },
                  "& .MuiDataGrid-virtualScroller": { width: "100%" },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Detail dialog: what changed + old/new values */}
        <Dialog
          open={Boolean(detailLog)}
          onClose={() => setDetailLog(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Audit log details</DialogTitle>
          <DialogContent dividers>
            {detailLog && (
              <Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  What changed
                </Typography>
                <Typography variant="body1" paragraph>
                  {detailLog.summary ||
                    `${detailLog.action} ${detailLog.page_label || detailLog.table_name} ${detailLog.record_id != null ? "#" + detailLog.record_id : ""}`.trim()}
                </Typography>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Page / Area
                </Typography>
                <Typography variant="body1" paragraph>
                  {detailLog.page_label || detailLog.table_name || "—"}
                </Typography>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  User · Action · When
                </Typography>
                <Typography variant="body2" paragraph>
                  {detailLog.user_name || "System"} · {detailLog.action} ·{" "}
                  {format(
                    new Date(detailLog.created_at),
                    "dd MMM yyyy HH:mm:ss",
                  )}
                </Typography>
                {detailLog.old_values && (
                  <>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Previous value
                    </Typography>
                    <Box
                      component="pre"
                      bgcolor="grey.100"
                      p={1.5}
                      borderRadius={1}
                      overflow="auto"
                      maxHeight={200}
                      fontSize="0.75rem"
                    >
                      {typeof detailLog.old_values === "string"
                        ? (() => {
                            try {
                              return JSON.stringify(
                                JSON.parse(detailLog.old_values),
                                null,
                                2,
                              );
                            } catch (_) {
                              return detailLog.old_values;
                            }
                          })()
                        : JSON.stringify(detailLog.old_values, null, 2)}
                    </Box>
                  </>
                )}
                {detailLog.new_values && (
                  <>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                      mt={2}
                    >
                      New value
                    </Typography>
                    <Box
                      component="pre"
                      bgcolor="grey.100"
                      p={1.5}
                      borderRadius={1}
                      overflow="auto"
                      maxHeight={200}
                      fontSize="0.75rem"
                    >
                      {typeof detailLog.new_values === "string"
                        ? (() => {
                            try {
                              return JSON.stringify(
                                JSON.parse(detailLog.new_values),
                                null,
                                2,
                              );
                            } catch (_) {
                              return detailLog.new_values;
                            }
                          })()
                        : JSON.stringify(detailLog.new_values, null, 2)}
                    </Box>
                  </>
                )}
                {!detailLog.old_values && !detailLog.new_values && (
                  <Typography variant="body2" color="textSecondary">
                    No previous/new value stored for this entry.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
  );
};

export default Audit;
